import { useContext, useEffect, useRef, useState } from "react";
import AttachFileRow from "../../../components/attachFileRow/attachFileRow";
import { AttachFileMode } from "../../../components/attachFileRow/types/attachFileProps";
import ButtonField from "../../../components/buttonField/buttonField";
import Modal from "../../../components/modalWindow/modal";
import { classNameBtn } from "../../../components/shared/button/types/buttonProps";
import SelectInput from "../../../components/shared/selectInput/selectInput";
import TextAreaField from "../../../components/textAreaField/textAreaField";
import { TextAreaFieldProps } from "../../../components/textAreaField/types/textAreaFieldProps";
import { Option } from "../../../components/shared/selectInput/types/selectInputProps";
import { configValidation } from "../../../helpers/validation/validation";
import { ReactComponent as MailIcon } from '../../../icons/mail.svg';
import { ReactComponent as AttachFile } from '../../../icons/attach_file.svg';
import { MessageAttachmentInfo } from "../../centralView/communicationView/messageView/types/readMessageViewProps";
import { WriteMessageProps } from "./types/writeMessageProps";
import { getUsers } from "../../../services/processService/processService";
import { notifyError, notifySuccess, notifyWarn } from "../../../components/snackbar/snackbar";
import { apiContext } from "../../../contexts/apiContext";
import Dropzone from "../../../components/dropzone/dropzone";
import { DropzoneProps } from "../../../components/dropzone/types/dropzoneProps";
import { sendMessage } from "../../../services/communicationService/communicationService";
import "./writeMessage.scss";
import AuthContext from "../../../contexts/authContext";
import { getUsersForMessages, setDictProposition, setUsersForMessages } from "../../../database/indexedDbReadWrite";

const WriteMessage = (props: WriteMessageProps) => {
    const api = useContext(apiContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectOptions, setSelectOptions] = useState<Option[]>([]);
    const [selectedOption, setSelectedOption] = useState<Option[]>([]);
    const [attachments, setAttachments] = useState<MessageAttachmentInfo[]>([]);
    const [content, setContent] = useState<string>('');
    const [receiverError, setReceiverError] = useState<boolean>(false);
    const [contentError, setContentError] = useState<boolean>(false);
    const { onlineMode, username } = useContext(AuthContext);

    const count = useRef(1);
    const maxFileNumberForUpload: any = 30;
    const totalMaxFileSize: any = 50*1048576;  // ~ 50 * 1 MB

    const send = () => {
        const receivers = selectedOption;
        if (receivers.length === 0) {
            setReceiverError(true);
            return;
        };
        if (!content) {
            setContentError(true);
            return;
        };
        if (content.length > 4000) { return; };
        setLoading(true);
        let data = {
            message: { TRESC: content },
            receivers: receivers.map(r => r.value),
            files: attachments.length > 0 ? attachments.map(a => a.blob) : null
        }
        if(!onlineMode){
            setDictProposition(username, data, "message").then((response) => {
                notifySuccess({ message: "Wiadomość została pomyślnie zapisana, zostanie zsynchronizowana po przejściu na tryb online" });
                close();
            }).catch(() => {
                notifyError({ message: "Napotkano błąd podczas zapisywania wiadomości" });
            }).finally(() => {
                setLoading(false);
            });
        }else{
            sendMessage(api.apiUrl + api.apiVersion, data)
            .then((res: any) => {
                setLoading(false);
                if (res === true) {
                    notifySuccess({ message: "Wiadomość została pomyślnie wysłana" });
                    close();
                } else {
                    notifyError({ message: "Wiadomość nie została wysłana" });
                }
            }).catch(() => {
                notifyError({ message: "Napotkano błąd podczas wysyłania wiadomości" });
                setLoading(false);
            })
        }
    }

    const close = () => {
        props.onClose();
    }

    const handleAttachmentClick = (value: any) => {
        const id = value.id;
        const currentAttachments = attachments.filter(a => a.id !== id);
        setAttachments(currentAttachments);
    }

    // Get all users
    useEffect(() => {
        setLoading(true);
        if(!onlineMode){
            getUsersForMessages().then((response) => {
                if(response){
                    prepareOptions(response);
                    setLoading(false);
                }
                else{
                    notifyWarn({ message: "Brak adresatów w bazie lokalnej, należy przejść w tryb online"});
                    setLoading(false); 
                }
            }).catch((error) => {
                notifyWarn({ message: "Błąd podczas pobierania użytkowników z bazy lokalnej"});
                setLoading(false);
            })
        }
        else{
            getUsers(api.apiUrl + api.apiVersion)
            .then((res: any) => {
                prepareOptions(res);
                setUsersForMessages(res).then((response) => {
                    
                });
                setLoading(false);
            }).catch(() => {
                notifyError({ message: `Próba pobrania listy użytkowników zakończona niepowodzeniem` });
                setLoading(false);
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prepareOptions = (data: any) => {
        var usersSelect: Option[] = data
            .filter((item: any) => item.nazwa !== null)
            .map((item: any) => {
                return {
                    value: item.nazwa,
                    name: `${item.nazwA_PELNA} (${item.nazwa})`
                };
            });
        setSelectOptions(usersSelect.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pl')));
    }

    const onChangeUser = (opt: Option[]) => {
        setSelectedOption(opt);
    }

    var validation: configValidation = {
        required: true,
        maxCharacters: {
            maxNumberOfCharacters: 4000,
            errorMsg: "Przekroczono limit 4 000 znaków"
        }
    }

    var textAreaProps: TextAreaFieldProps = {
        labelName: '',
        textAreaPlaceholder: 'Wpisz wiadomość',
        value: content,
        textareaSharedProps: {
            className: 'body-txt-medium',
            style: { width: '100%' }
        },
        configValidation: validation,
        disabled: false,
        setValue: (value: any) => {
            setContent(value);
        },
        dynamicResize: true,
        validationError: contentError,
        tipText: 'To pole jest wymagane',
        pristine: true
    }

    const handleFileConfirm = (files: File[]) => {
        const currCount = attachments.length + files.length;
        const currFileSize = calculateSize(files);
        if (currCount > maxFileNumberForUpload) {
            notifyError({ message: `Próba dodania plików zakończona niepowodzeniem - maksymalna liczba wgrywanych plików to ${maxFileNumberForUpload}` });
        } else if (currFileSize > totalMaxFileSize) {
            notifyError({ message: "Przekroczono dopuszczalny rozmiar załączników (50MB)" });
        } else {
            const newAttachments: MessageAttachmentInfo[] = files.map((file: any, index: number) => {
                return { id: count.current + index, name: file.name, url: "", blob: file };
            });
            const currAttachments = attachments.concat(newAttachments);
            setAttachments(currAttachments);
            count.current = count.current + 1 + newAttachments.length;
        }
    }

    const calculateSize = (files: File[]) => {
        const prevFileSize = attachments.reduce((acc, obj) => { return acc + obj.blob.size; }, 0);
        const newFileSize = files.reduce((acc, obj) => { return acc + obj.size; }, 0);
        return prevFileSize + newFileSize;
    }

    var imageDropzoneProps: DropzoneProps = {
        multiple: true,
        disabled: false,
        acceptedFiles: [
            "application/pdf",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ".doc",
            ".docx",
            "application/xml",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/*"
        ],
        onConfirm: handleFileConfirm,
        acceptedFilesForSnackbar: "pdf, word, excel lub pliki graficzne",
        hidden: true
    }

    return (
        <Modal
            title="Napisz wiadomość"
            titleIcon={MailIcon}
            iconClass='titleIconPrimary'
            width={'660px'}
            height={'90vh'}
            loading={loading}
            actionBtns={[
                <ButtonField
                    key={1}
                    BtnProps={{
                        onClick: send,
                        className: `${classNameBtn.Btn} ${classNameBtn.Large} body-txt-medium`
                    }}
                    placeHolder="Wyślij wiadomość"
                />
            ]}
            onClose={close}>
            <div className="writeMsg-container">
                <SelectInput label="Wpisz adresata lub wybierz z listy" placeHolder="Wpisz imię i nazwisko" hasInputTags={true} options={selectOptions} onlySingleTag={false}
                    configValidation={{ required: true }} onChange={onChangeUser} pristine={true} disabled={loading} validationError={receiverError} tipText='To pole jest wymagane' />
                <TextAreaField {...textAreaProps} />
                <div className="writeMsg-attach-item">
                    {attachments.map(attachment => {
                        return <AttachFileRow key={attachment.id} mode={AttachFileMode.Add} info={attachment} onClick={handleAttachmentClick} />
                    })}
                </div>
                <Dropzone {...imageDropzoneProps} >
                    <div className="writeMsg-attach"><AttachFile /><div className="body-txt-large">Dodaj załącznik</div></div>
                </Dropzone>
            </div>
        </Modal>
    )
}

export default WriteMessage;
